import { useCallback, useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import type { IScannerControls } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { getErrorToast } from "../../../../../infrastructure/utilities/toastUtility";

/**
 * States:
 *   initializing — requesting user media, waiting for the stream.
 *   scanning     — live video decode loop is running.
 *   fallback     — getUserMedia is unsupported on this device; use a file
 *                  input backed by the OS camera app (`capture="environment"`).
 *   decoding     — a static image from the fallback flow is being decoded.
 *   error        — non-fatal error shown inside the modal (e.g. last photo
 *                  could not be decoded). The modal stays open.
 *
 * Permission denial is NOT a state here — the hook reports it via
 * `onPermissionDenied` so the modal can close itself and surface a toast,
 * and the next time the user opens the modal we re-request permission.
 */
export type BarcodeScannerState =
  | "initializing"
  | "scanning"
  | "fallback"
  | "decoding"
  | "error";

interface IUseBarcodeScannerOptions {
  enabled: boolean;
  onScanned: (value: string) => void;
  onPermissionDenied: () => void;
}

interface IUseBarcodeScannerResult {
  videoRef: React.RefObject<HTMLVideoElement>;
  state: BarcodeScannerState;
  errorMessage?: string;
  decodeFromFile: (file: File) => Promise<void>;
}

const isPermissionDeniedError = (err: unknown): boolean => {
  if (!err || typeof err !== "object") return false;
  const name = (err as { name?: string }).name;
  return name === "NotAllowedError" || name === "PermissionDeniedError";
};

const isMediaSupported = (): boolean =>
  typeof navigator !== "undefined" &&
  !!navigator.mediaDevices &&
  typeof navigator.mediaDevices.getUserMedia === "function";

/**
 * Polls until the ref holds a mounted HTMLVideoElement.
 * PrimeReact Dialog uses a portal that may mount children after the
 * initial render cycle, so the ref can still be null when effects fire.
 */
const waitForVideoRef = (
  ref: React.RefObject<HTMLVideoElement>,
  signal: { cancelled: boolean }
): Promise<HTMLVideoElement | null> =>
  new Promise((resolve) => {
    if (ref.current) {
      resolve(ref.current);
      return;
    }
    let attempts = 0;
    const check = () => {
      if (signal.cancelled) {
        resolve(null);
        return;
      }
      if (ref.current) {
        resolve(ref.current);
        return;
      }
      if (++attempts > 30) {
        // ~500 ms at 60 fps — give up
        resolve(null);
        return;
      }
      requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  });

export const useBarcodeScanner = ({
  enabled,
  onScanned,
  onPermissionDenied,
}: IUseBarcodeScannerOptions): IUseBarcodeScannerResult => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const scannedRef = useRef(false);

  const [state, setState] = useState<BarcodeScannerState>("initializing");
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  // Latest callbacks in refs so the start-scan effect stays stable.
  const onScannedRef = useRef(onScanned);
  const onPermissionDeniedRef = useRef(onPermissionDenied);
  useEffect(() => {
    onScannedRef.current = onScanned;
    onPermissionDeniedRef.current = onPermissionDenied;
  }, [onScanned, onPermissionDenied]);

  const stopLiveStream = useCallback(() => {
    try {
      controlsRef.current?.stop();
    } catch {
      // ignore — controls already stopped
    }
    controlsRef.current = null;

    const video = videoRef.current;
    if (video && video.srcObject instanceof MediaStream) {
      video.srcObject.getTracks().forEach((track) => track.stop());
      video.srcObject = null;
    }
  }, []);

  const handleSuccess = useCallback((value: string) => {
    if (scannedRef.current) return;
    scannedRef.current = true;
    onScannedRef.current(value);
  }, []);

  useEffect(() => {
    if (!enabled) {
      stopLiveStream();
      scannedRef.current = false;
      setState("initializing");
      setErrorMessage(undefined);
      return;
    }

    // Fresh session — start over.
    scannedRef.current = false;
    setErrorMessage(undefined);

    if (!isMediaSupported()) {
      setState("fallback");
      return;
    }

    setState("initializing");

    if (!readerRef.current) {
      const hints = new Map();
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.EAN_13,
        BarcodeFormat.EAN_8,
        BarcodeFormat.UPC_A,
        BarcodeFormat.UPC_E,
        BarcodeFormat.CODE_128,
        BarcodeFormat.CODE_39,
        BarcodeFormat.QR_CODE,
        BarcodeFormat.ITF,
      ]);
      readerRef.current = new BrowserMultiFormatReader(hints, {
        delayBetweenScanAttempts: 50,
      });
    }
    const reader = readerRef.current;

    const signal = { cancelled: false };

    const start = async () => {
      try {
        // Wait for the <video> element to be mounted in the DOM.
        // PrimeReact Dialog renders via a portal that can lag behind
        // the effect by one or more frames — passing null/undefined
        // causes @zxing to create a detached element (black screen).
        const video = await waitForVideoRef(videoRef, signal);

        if (signal.cancelled) return;

        if (!video) {
          setState("fallback");
          return;
        }

        const controls = await reader.decodeFromConstraints(
          {
            video: {
              facingMode: { ideal: "environment" },
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
          },
          video,
          (result) => {
            if (!result) return;
            const text = result.getText();
            if (!text) return;
            stopLiveStream();
            handleSuccess(text);
          }
        );

        if (signal.cancelled) {
          try {
            controls.stop();
          } catch {
            // ignore
          }
          return;
        }

        controlsRef.current = controls;
        setState("scanning");
      } catch (err) {
        if (signal.cancelled) return;

        if (isPermissionDeniedError(err)) {
          stopLiveStream();
          getErrorToast(
            "Достъпът до камерата е отказан. Можете да въведете баркода ръчно или да опитате отново."
          );
          onPermissionDeniedRef.current();
          return;
        }

        // Any other getUserMedia failure (no device, hardware busy, etc.)
        // → transparently fall back to the OS camera input so the user can
        // still take a picture of the barcode.
        setState("fallback");
      }
    };

    void start();

    return () => {
      signal.cancelled = true;
      stopLiveStream();
    };
  }, [enabled, stopLiveStream, handleSuccess]);

  // Release the reader entirely on unmount so we don't leak decoders.
  useEffect(
    () => () => {
      stopLiveStream();
      readerRef.current = null;
    },
    [stopLiveStream]
  );

  const decodeFromFile = useCallback(
    async (file: File) => {
      if (scannedRef.current) return;

      if (!readerRef.current) {
        const hints = new Map();
        hints.set(DecodeHintType.TRY_HARDER, true);
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
          BarcodeFormat.EAN_13,
          BarcodeFormat.EAN_8,
          BarcodeFormat.UPC_A,
          BarcodeFormat.UPC_E,
          BarcodeFormat.CODE_128,
          BarcodeFormat.CODE_39,
          BarcodeFormat.QR_CODE,
          BarcodeFormat.ITF,
        ]);
        readerRef.current = new BrowserMultiFormatReader(hints);
      }
      const reader = readerRef.current;

      setState("decoding");
      setErrorMessage(undefined);

      const objectUrl = URL.createObjectURL(file);
      try {
        const result = await reader.decodeFromImageUrl(objectUrl);
        const text = result?.getText();
        if (!text) {
          throw new Error("empty result");
        }
        handleSuccess(text);
      } catch (err) {
        setState("error");
        const message =
          "Не успяхме да разчетем баркода от снимката. Опитайте отново.";
        setErrorMessage(message);
        getErrorToast(message);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    },
    [handleSuccess]
  );

  return { videoRef, state, errorMessage, decodeFromFile };
};
